// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import {
  aircraftMakeModelLabel,
  engineLabel,
  monitorLabel,
} from "./aircraft-profile-model.js";
import {
  displayMonitorLabel,
  monitorLabelForParser,
} from "./supported-monitors.js";

const FORMAT_ONLY_PARSER_IDS = ["garmin-user-log", "jpi-binary", "jpi-csv"];
const FORMAT_ONLY_MONITOR_LABELS = new Set(
  FORMAT_ONLY_PARSER_IDS
    .map((parserId) => monitorLabelForParser(parserId))
    .filter((label) => typeof label === "string" && label)
    .map((label) => label.toLowerCase()),
);

export function finiteNumber(value) {
  if (Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

export function latestFlightFromAircraftFlights(flights = [], {
  isSupportedFlight = () => false,
} = {}) {
  return flights.find((entry) => isSupportedFlight(entry)) ?? flights[0] ?? null;
}

export function aircraftFlightDurationSeconds(entry) {
  const segmentDuration = finiteNumber(entry?.segment?.durationSeconds);
  if (Number.isFinite(segmentDuration)) return Math.max(0, segmentDuration);
  const jpiDuration = finiteNumber(entry?.jpiFlight?.durationSeconds);
  if (Number.isFinite(jpiDuration)) return Math.max(0, jpiDuration);
  const rawDuration = finiteNumber(entry?.duration_seconds);
  if (Number.isFinite(rawDuration)) return Math.max(0, rawDuration);
  const manifestDuration = finiteNumber(entry?.durationSeconds);
  if (Number.isFinite(manifestDuration)) return Math.max(0, manifestDuration);
  return null;
}

export function aircraftFlightDurationSecondsFromTrendRows(entry, rows = []) {
  const directDuration = aircraftFlightDurationSeconds(entry);
  if (Number.isFinite(directDuration)) return directDuration;
  if (!entry || !Array.isArray(rows) || !rows.length) return null;
  const entryIds = new Set([
    entry.id,
    entry.database?.flightId,
  ].filter(Boolean).map(String));
  const entryTitles = new Set([
    entry.title,
    entry.displayTitle,
    entry.flightTitle,
  ].filter((value) => typeof value === "string" && value.trim()));
  const match = rows.find((row) => {
    if (!row || typeof row !== "object") return false;
    if (entryIds.has(String(row.id ?? "")) || entryIds.has(String(row.flightId ?? ""))) return true;
    return [row.title, row.displayTitle, row.flightTitle]
      .some((value) => typeof value === "string" && entryTitles.has(value));
  });
  const trendSeconds = finiteNumber(match?.durationSeconds);
  if (Number.isFinite(trendSeconds)) return Math.max(0, trendSeconds);
  const trendMinutes = finiteNumber(match?.durationMinutes);
  if (Number.isFinite(trendMinutes)) return Math.max(0, trendMinutes * 60);
  return null;
}

export function aircraftAverageFlightDurationSeconds(flights = [], rows = []) {
  const durations = flights
    .map((entry) => aircraftFlightDurationSecondsFromTrendRows(entry, rows))
    .filter(Number.isFinite);
  if (!durations.length) return null;
  return durations.reduce((sum, value) => sum + value, 0) / durations.length;
}

export function aircraftTotalFlightDurationSeconds(flights = [], rows = []) {
  const durations = flights
    .map((entry) => aircraftFlightDurationSecondsFromTrendRows(entry, rows))
    .filter(Number.isFinite);
  if (!durations.length) return null;
  return durations.reduce((sum, value) => sum + value, 0);
}

// Match flightDateLabel's display zone (America/Phoenix) so the "Date range"
// card and the "Latest flight" card never show different dates for the same
// flight. trendDateValue returns real epoch timestamps for any flight with a
// start time, and anchors date-only sources at noon UTC, so both stay on the
// intended calendar day in this zone.
export function aircraftFlightRangeDateLabel(value) {
  if (!Number.isFinite(value) || value <= 0) return "Unknown";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "America/Phoenix",
  }).format(new Date(value));
}

// Each date stays on one line in a tight card: its internal spaces become
// non-breaking, so a range wraps only around the dash (two lines at most)
// instead of splitting "Oct 17," from "2025".
function atomicDateLabel(label) {
  return String(label).replace(/ /g, "\u00A0");
}

export function aircraftFlightDateRangeLabel(flights = [], {
  trendDateValue = () => null,
} = {}) {
  const values = flights
    .map((entry) => trendDateValue(entry, []))
    .filter(Number.isFinite);
  if (!values.length) return "Dates unavailable";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const start = aircraftFlightRangeDateLabel(min);
  const end = aircraftFlightRangeDateLabel(max);
  return start === end ? atomicDateLabel(start) : `${atomicDateLabel(start)} - ${atomicDateLabel(end)}`;
}

export function aircraftTrendDateRangeLabel(rows = []) {
  const values = rows
    .map((row) => finiteNumber(row?.dateValue))
    .filter((value) => Number.isFinite(value) && value > 0);
  if (!values.length) return "Dates unavailable";
  const min = Math.min(...values);
  const max = Math.max(...values);
  const start = aircraftFlightRangeDateLabel(min);
  const end = aircraftFlightRangeDateLabel(max);
  return start === end ? start : `${start} - ${end}`;
}

export function aircraftTrendCurrentFlightCard({
  aircraft,
  rows = [],
  selectedFlight = null,
  latestFlight = null,
  flightDateLabel = () => "Date unavailable",
  flightDisplayTitle = () => "Flight log",
} = {}) {
  const selectedRow = selectedFlight?.aircraft === aircraft
    ? rows.find((row) => row.id === selectedFlight.id)
    : null;
  const fallbackRow = rows.filter((row) => !row.error).at(-1) ?? rows.at(-1);
  const row = selectedRow ?? fallbackRow;
  if (row) {
    const date = finiteNumber(row.dateValue) ? aircraftFlightRangeDateLabel(row.dateValue) : row.dateLabel ?? "Date unavailable";
    return {
      label: selectedRow ? "Current flight" : "Latest trend flight",
      value: date,
      detail: row.title ?? "Trend row",
      accent: "current",
      tone: "hero",
    };
  }
  return {
    label: "Current flight",
    value: selectedFlight?.aircraft === aircraft ? flightDateLabel(null, selectedFlight) : "Not analyzed yet",
    detail: selectedFlight?.aircraft === aircraft ? flightDisplayTitle(selectedFlight) : latestFlight ? flightDisplayTitle(latestFlight) : "Run trend analysis to populate this view",
    accent: "current",
    tone: "hero",
  };
}

export function aircraftTrendActivityCards({
  aircraft,
  flights = [],
  rows = null,
  selectedFlight = null,
  latestFlight = null,
  isSupportedFlight = () => false,
  buildTrendSignalSummaries = () => [],
  flightDateLabel = () => "Date unavailable",
  flightDisplayTitle = () => "Flight log",
  trendDateValue = () => null,
} = {}) {
  const supportedFlights = flights.filter((entry) => isSupportedFlight(entry));
  const usableRows = (rows ?? []).filter((row) => !row.error);
  const errorCount = (rows ?? []).filter((row) => row.error).length;
  const signals = usableRows.length ? buildTrendSignalSummaries(usableRows) : [];
  // The header stat counts only areas still carrying a non-green status; recovered
  // (green) signals stay out so the count matches the rail's watch/alert tiles.
  const reviewSignals = signals.filter((signal) => signal?.status?.className !== "green");
  const topSignal = reviewSignals[0];
  const rowsReady = Array.isArray(rows);
  return [
    {
      label: "Trend flights",
      value: rowsReady ? String(usableRows.length) : String(supportedFlights.length),
      detail: rowsReady
        ? errorCount
          ? `${errorCount} skipped during analysis`
          : "Usable flights in this trend set"
        : "Ready for trend analysis",
      accent: "trend-count",
      tone: "default",
    },
    aircraftTrendCurrentFlightCard({
      aircraft,
      rows: rows ?? [],
      selectedFlight,
      latestFlight,
      flightDateLabel,
      flightDisplayTitle,
    }),
    {
      label: "Date range",
      value: rowsReady ? aircraftTrendDateRangeLabel(rows) : aircraftFlightDateRangeLabel(supportedFlights, { trendDateValue }),
      detail: rowsReady ? "Oldest to newest analyzed row" : "Oldest to newest supported log",
      accent: "range",
      tone: "default",
    },
    {
      label: "Analysis coverage",
      value: rowsReady ? `${usableRows.length}/${supportedFlights.length}` : "--",
      detail: rowsReady ? "Usable trend rows vs supported logs" : "Run trend analysis to measure coverage",
      accent: "coverage",
      tone: "default",
    },
    {
      label: "Areas to review",
      value: rowsReady ? String(reviewSignals.length) : "--",
      detail: topSignal ? `${topSignal.status.label}: ${topSignal.label}` : rowsReady ? "Steady in analyzed history" : "Waiting for trend rows",
      accent: "signals",
      tone: "default",
    },
  ];
}

export function aircraftAverageFlightDurationFromTrendRows(rows = []) {
  const durations = rows
    .map((row) => finiteNumber(row?.durationMinutes))
    .filter(Number.isFinite)
    .map((minutes) => minutes * 60);
  if (!durations.length) return null;
  return durations.reduce((sum, value) => sum + value, 0) / durations.length;
}

export function aircraftRecentFlightCount(flights = [], days = 90, {
  now = Date.now(),
  trendDateValue = () => null,
} = {}) {
  if (!flights.length) return 0;
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  return flights.filter((entry) => {
    const value = trendDateValue(entry, []);
    return Number.isFinite(value) && value >= cutoff;
  }).length;
}

export function aircraftProfileHealthLabel(profile, latestFlight) {
  const configuredCount = [
    aircraftMakeModelLabel(profile),
    engineLabel(profile),
    monitorLabel(profile, latestFlight?.monitor ?? ""),
  ].filter((value) => typeof value === "string" && value.trim()).length;
  if (configuredCount >= 3) return "Ready";
  if (configuredCount === 2) return "Almost ready";
  if (configuredCount === 1) return "Needs setup";
  return "Profile pending";
}

function usableFlightMonitor(flights = [], latestFlight = null) {
  return [latestFlight, ...flights]
    .map((entry) => (typeof entry?.monitor === "string" ? entry.monitor.trim() : ""))
    .find((monitor) => {
      if (!monitor) return false;
      const lower = monitor.toLowerCase();
      if (lower === "unknown") return false;
      if (FORMAT_ONLY_MONITOR_LABELS.has(lower)) return false;
      return true;
    }) ?? "";
}

export function aircraftFleetRowDetail(profile, flights = [], latestFlight = null) {
  const count = Array.isArray(flights) ? flights.length : 0;
  const fallbackMonitor = usableFlightMonitor(flights, latestFlight);
  const resolvedMonitor = monitorLabel(profile, fallbackMonitor);
  const hasMonitor = typeof resolvedMonitor === "string" && resolvedMonitor.trim() && resolvedMonitor.trim().toLowerCase() !== "unknown";
  return {
    logs: `${count} log${count === 1 ? "" : "s"}`,
    monitor: hasMonitor ? displayMonitorLabel(resolvedMonitor) : "Monitor not set",
    engine: engineLabel(profile) ?? "Engine not set",
  };
}

export function aircraftActivityCardsForPanel({
  selectedAircraftPanel = "profile",
  aircraft,
  flights = [],
  latestFlight,
  flightDateText,
  recentFlightCount,
  averageFlightDuration,
  setupStatus,
  makeModelText,
  uploadHistory = [],
  rows = null,
  selectedFlight = null,
  isSupportedFlight = () => false,
  flightFlagSummary = () => null,
  buildTrendSignalSummaries = () => [],
  flightDateLabel = () => "Date unavailable",
  flightDisplayTitle = () => "Flight log",
  trendDateValue = () => null,
  formatCompactElapsedTime = () => "--",
} = {}) {
  if (!aircraft) return [];
  if (selectedAircraftPanel === "trends") {
    return aircraftTrendActivityCards({
      aircraft,
      flights,
      rows,
      selectedFlight,
      latestFlight,
      isSupportedFlight,
      buildTrendSignalSummaries,
      flightDateLabel,
      flightDisplayTitle,
      trendDateValue,
    });
  }
  if (selectedAircraftPanel === "flights") {
    const totalFlightDuration = aircraftTotalFlightDurationSeconds(flights, rows ?? []);
    const supportedFlights = flights.filter((entry) => isSupportedFlight(entry)).length;
    // Count every flagged CHT Temp / EGT Rhythm result across the visible
    // flights — the per-row pills in the table, totalled. Only "ready"
    // summaries count; loading/not-analyzed rows contribute nothing.
    const flaggedCount = flights.reduce((count, entry) => {
      const summary = flightFlagSummary(entry);
      if (summary?.state !== "ready") return count;
      return count + (summary.highCht ? 1 : 0) + (summary.egtRhythm ? 1 : 0);
    }, 0);
    return [
      {
        label: "Flights",
        value: String(flights.length),
        // Populated cards carry no explanatory detail: the label already says
        // what the number is. Empty states keep theirs, which tell the user what
        // to DO rather than restating the metric.
        detail: latestFlight ? "" : "Add a log to begin flight history",
        accent: "count",
        tone: "default",
      },
      {
        label: "Date range",
        value: aircraftFlightDateRangeLabel(flights, { trendDateValue }),
        detail: flights.length ? "" : "No dated flights yet",
        accent: "range",
        tone: "default",
      },
      {
        label: "CHT / EGT Flags",
        value: String(flaggedCount),
        detail: flights.length ? "" : "No imported flights yet",
        accent: "status",
        tone: flaggedCount ? "hero" : "default",
      },
      {
        label: "Total logged",
        value: Number.isFinite(totalFlightDuration) ? formatCompactElapsedTime(totalFlightDuration) : "--",
        detail: Number.isFinite(totalFlightDuration) ? "" : "Timing unavailable in visible logs",
        accent: "total",
        tone: "default",
      },
      {
        label: "Average duration",
        value: averageFlightDuration ? formatCompactElapsedTime(averageFlightDuration) : "--",
        detail: averageFlightDuration ? "" : "Duration becomes available as logs accumulate",
        accent: "duration",
        tone: "default",
      },
      {
        label: "Reviewable logs",
        value: `${supportedFlights}/${flights.length}`,
        detail: flights.length === supportedFlights ? "" : "Some logs need parser support",
        accent: "supported",
        tone: "default",
      },
    ];
  }
  if (selectedAircraftPanel === "uploads") {
    const uploads = Array.isArray(uploadHistory) ? uploadHistory : [];
    const completed = uploads.filter((upload) => ["available", "completed"].includes(upload.upload_status)).length;
    const active = uploads.filter((upload) => ["queued", "processing", "received"].includes(upload.upload_status)).length;
    const needsReview = uploads.filter((upload) => ["failed", "unsupported"].includes(upload.upload_status)).length;
    const latest = uploads[0] ?? null;
    return [
      {
        label: "Uploads",
        value: String(uploads.length),
        detail: uploads.length ? "Raw files preserved for this aircraft" : "No uploads yet",
        accent: "count",
        tone: "default",
      },
      {
        label: "Available",
        value: String(completed),
        detail: completed ? "Ready to open from this aircraft" : "Available uploads will appear here",
        accent: "supported",
        tone: "default",
      },
      {
        label: "Processing",
        value: String(active),
        detail: active ? "Queued or running ingest jobs" : "No active ingest jobs",
        accent: "duration",
        tone: active ? "hero" : "default",
      },
      {
        label: "Needs review",
        value: String(needsReview),
        detail: needsReview ? "Failed or unsupported uploads" : "No failed uploads in history",
        accent: "status",
        tone: needsReview ? "hero" : "default",
      },
      {
        label: "Latest raw file",
        value: latest?.original_filename ?? "--",
        detail: latest?.upload_status ? `Status: ${latest.upload_status}` : "Upload a log to start history",
        accent: "latest",
        tone: "default",
      },
    ];
  }
  return [
    {
      label: "Flights",
      value: String(flights.length),
      detail: latestFlight ? "Visible logs for this aircraft" : "Add a log to begin trend history",
      accent: "count",
      tone: "default",
    },
    {
      label: "Latest flight",
      value: flightDateText,
      detail: latestFlight ? flightDisplayTitle(latestFlight) : "No imported flights yet",
      accent: "latest",
      tone: "hero",
    },
    {
      label: "Recent activity",
      value: `${recentFlightCount}`,
      detail: recentFlightCount
        ? "Flights in the last 90 days"
        : "No dated flights in the last 90 days",
      accent: "recent",
      tone: "default",
    },
    {
      label: "Average duration",
      value: averageFlightDuration ? formatCompactElapsedTime(averageFlightDuration) : "--",
      detail: averageFlightDuration ? "Across flights with usable timing" : "Duration becomes available as logs accumulate",
      accent: "duration",
      tone: "default",
    },
    setupStatus === "Ready" ? null : {
      label: "Profile status",
      value: setupStatus,
      detail: makeModelText,
      accent: "status",
      tone: "default",
    },
  ].filter(Boolean);
}
